import { LANGSMITH_HEADER_NAMES, redactLangSmithKey } from "./langsmithKey";

/**
 * Keeps the LangSmith key out of what the admin app sends to Sentry
 * (GEN5-5344, K2). Sentry's browser SDK does not record request headers by
 * default, but an event or breadcrumb can still carry one (a request object
 * someone attached, an error message that quoted a header). So, on the way
 * out: any field named like a LangSmith header is replaced, and the key in
 * use, or anything shaped like a LangSmith key, is cut out of every string.
 */

const REMOVED = "[removed]";
const HEADER_NAMES = new Set(
  LANGSMITH_HEADER_NAMES.map((name) => name.toLowerCase())
);
/** LangSmith's key formats: personal tokens and service keys. */
const KEY_SHAPE = /lsv2_(?:pt|sk)_[A-Za-z0-9_]+/g;

const cleanText = (text: string): string =>
  redactLangSmithKey(text).replace(KEY_SHAPE, "[LangSmith key removed]");

/**
 * Rewrites, in place, every string in the event and every field named like a
 * LangSmith header. In place because Sentry hands the hook its own object and
 * expects it back; the walk is the only writer, and it stops at cycles.
 */
export const removeLangSmithSecrets = <Event extends object>(
  event: Event
): Event => {
  const seen = new WeakSet<object>();
  const walk = (container: object): void => {
    if (seen.has(container)) return;
    seen.add(container);
    for (const field of Object.keys(container)) {
      const value: unknown = Reflect.get(container, field);
      if (HEADER_NAMES.has(field.toLowerCase())) {
        Reflect.set(container, field, REMOVED);
      } else if (typeof value === "string") {
        const cleaned = cleanText(value);
        if (cleaned !== value) Reflect.set(container, field, cleaned);
      } else if (typeof value === "object" && value !== null) {
        walk(value);
      }
    }
  };
  walk(event);
  return event;
};
